/** The town's street plan: what turns a row of buildings into a place.
 *
 * A town screen that is only sprites on grass reads as a display shelf. What
 * fixes it is not more art, it is *circulation*: a cobbled street per depth
 * band, alleys linking the bands, and a spur from every built structure down to
 * the street in front of it. The eye then reads ground plane first and
 * buildings standing on it second.
 *
 * Two rules keep the ground reading as ground rather than as tape laid over the
 * grass:
 *
 *  - **Everything that runs into depth runs at 2:1.** The scene is drawn in
 *    three-quarter isometric, so a path that recedes moves two pixels sideways
 *    for every pixel of depth. A spur drawn straight down the screen is a
 *    vertical wall face, which is exactly what the previous plan looked like.
 *  - **No way ends in the air.** The three streets are joined at both ends by
 *    doglegged connectors, so the network is a closed ring: no road leaves the
 *    frame and none stops against a wall.
 *
 * The geometry is derived from `TOWN_SLOTS`, so moving a slot moves its
 * approach with it and the two can never drift apart. Pure: no DOM, no React,
 * no kernel rule module, and no randomness - the wobble that keeps a kerb from
 * looking ruler-drawn is a hash of the point, so two runs draw the same town.
 */

import type { TownSlot } from "./townSlots.js";
import { BAND_SCALE, TOWN_CANVAS_H, TOWN_CANVAS_W, drawnWidth } from "./townSlots.js";

export interface PathPoint {
  readonly x: number;
  readonly y: number;
}

/** A closed polygon in canvas coordinates, ready for one `<polygon>`. */
export interface PathPolygon {
  readonly points: readonly PathPoint[];
}

/**
 * A paved figure plus the depth band whose stone size it is drawn at.
 *
 * The stones have to shrink with distance or the whole depth ramp the band
 * scaling builds is contradicted by a floor whose texture is the same size at
 * the viewer's feet as it is four hundred pixels into the scene.
 */
export interface Way extends PathPolygon {
  readonly band: TownSlot["band"];
}

export interface TownPathPlan {
  /** Streets, alleys and spurs, in the order they are painted. */
  readonly ways: readonly Way[];
  /**
   * The worn earth each way sits in, one per way and in the same order.
   *
   * Paving that meets grass on a hard line is a decal. What a real road has is
   * a margin of trodden dirt either side of it, so every way is drawn twice:
   * the verge first, wider and soft, then the stones.
   */
  readonly verges: readonly PathPolygon[];
  /**
   * A wider, fainter ring outside the verge, one per way.
   *
   * The verge stops the paving being a decal; the halo stops the VERGE being
   * one. A road's influence on the ground either side of it does not end on a
   * line - what ends on a line is a polygon - so a third ring is laid at
   * roughly three times the verge's margin and a third of its weight, and the
   * three together read as trodden earth thinning out into grass.
   */
  readonly halos: readonly PathPolygon[];
}

/** A point on a way: centre line plus the width of the ribbon there. */
interface WayPoint {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

// ------------------------------------------------------------ the three streets

/**
 * Where each band's street runs and how wide it is.
 *
 * Wider the nearer it is, which is the same perspective the band scales imply,
 * and never narrower than a doorway with its steps - a lane a person could not
 * walk down reads as a garden wall, not as a road.
 */
interface StreetSpec {
  readonly y: number;
  readonly w: number;
  readonly from: number;
  readonly to: number;
  /** How much the lane sags between its ends, so it is not a ruled line. */
  readonly sag: number;
  /**
   * A second, shorter wave laid over the sag.
   *
   * One sine over a 1294-unit span is an arc, and an arc read across a frame
   * this wide is a ruled line with a bend in it - measured at 9 to 12 canvas
   * pixels of deflection, which is under one per cent of the span. A road
   * meanders because the ground under it does, so the second term runs at
   * about three times the frequency and out of phase, and the two together
   * put a lane's centre 20 to 38 pixels off the straight line it would
   * otherwise be.
   */
  readonly wave: number;
  readonly phase: number;
}

/**
 * Full bleed, both ends, all three.
 *
 * A lane that stops sixty pixels short of the frame has an end, and an end in
 * the middle of a lawn is the single loudest "this is a debug overlay" tell a
 * town screen can carry. A street that leaves the frame has no end to explain.
 * The third entry is the near promenade's centre line: the promenade itself is
 * a full-width apron (see `plaza`), and this line is what the front band's
 * approaches run down to.
 */
const STREET_SPECS: readonly StreetSpec[] = Object.freeze([
  { y: 352, w: 21, from: -7, to: 1287, sag: 24, wave: 8, phase: 0.17 },
  { y: 560, w: 31, from: -7, to: 1287, sag: 27, wave: 11, phase: 0.63 },
  { y: 700, w: 41, from: -7, to: 1287, sag: 15, wave: 9, phase: 0.31 },
]);

/** A street as a centre line: ends tapered, so a lane peters out into grass. */
function streetLine(spec: StreetSpec): readonly WayPoint[] {
  // Eighteen samples rather than eight: the second wave turns over three times
  // across the frame, and eight samples draw a triangle wave through it.
  const steps = 18;
  const out: WayPoint[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = spec.from + (spec.to - spec.from) * t;
    // A shallow arc, deepest in the middle: the lane bows toward the viewer.
    // The second term meanders across it; it is damped at the two ends so the
    // lane still leaves the frame square rather than at an angle.
    const y = spec.y + spec.sag * Math.sin(Math.PI * t)
      + spec.wave * Math.sin(Math.PI * (2.7 * t + spec.phase)) * Math.sin(Math.PI * t);
    // A gentle swell toward the middle - never a taper to a point, because a
    // street that runs off the frame has no end that wants narrowing. The
    // width breathes on its own period, so the kerbs are not parallel.
    const taper = 0.86 + 0.14 * Math.sin(Math.PI * t)
      + 0.07 * Math.sin(Math.PI * (3.3 * t + spec.phase * 2));
    out.push({ x, y, w: spec.w * taper });
  }
  return out;
}

const STREETS: readonly (readonly WayPoint[])[] = Object.freeze(
  STREET_SPECS.map(streetLine),
);

/**
 * Where a street's centre line runs at a given x.
 *
 * The streets sag across the canvas so they read as lanes rather than as ruled
 * lines, which means "the y of the street" is a function of x. Spurs and alleys
 * sample it; joining them to one nominal y is what leaves a spur ending in
 * mid-air a few pixels short of the road.
 */
function centreLineAt(way: readonly WayPoint[], x: number): number {
  const first = way[0]!;
  const last = way[way.length - 1]!;
  if (x <= first.x) return first.y;
  if (x >= last.x) return last.y;
  for (let i = 1; i < way.length; i++) {
    const a = way[i - 1]!;
    const b = way[i]!;
    if (x <= b.x) return a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x || 1);
  }
  return last.y;
}

// ------------------------------------------------------------ isometric runs

/** The scene's projection: two across for one down. Everything obeys it. */
const ISO = 2;

/**
 * A run of depth `dy` starting at `x`, leaning `dir` (+1 right, -1 left).
 *
 * One straight 2:1 segment. Used for anything short enough that the sideways
 * travel stays on the canvas - spurs, and the alleys between bands.
 */
const isoRun = (x: number, dy: number, dir: number): number => x + dir * ISO * dy;

/** Give a polyline a width profile, so it can be turned into a ribbon. */
function widen(pts: readonly PathPoint[], from: number, to: number): readonly WayPoint[] {
  const n = pts.length;
  return pts.map((p, i) => ({ x: p.x, y: p.y, w: from + ((to - from) * i) / (n - 1 || 1) }));
}

// --------------------------------------------------------------- ribbons

/** Deterministic +-2px jitter, so a kerb is not a ruler line. */
function wobble(seed: number, amount: number): number {
  let h = Math.trunc(seed) * 2654435761;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((Math.abs(h) % 256) / 255 * 2 - 1) * amount;
}

/**
 * A centre line with widths becomes a closed polygon: left edge, then right back.
 *
 * `grow` widens the whole ribbon, which is how the worn verge under a way is
 * built from the same centre line - the two can never drift apart.
 * `resolution` splits every segment, so the wobble lands often enough to read
 * as an irregular edge rather than as a bevelled one.
 */
function ribbon(pts: readonly WayPoint[], grow = 0, jitter = 2): PathPolygon {
  const dense = densify(pts);
  const left: PathPoint[] = [];
  const right: PathPoint[] = [];
  const n = dense.length;
  for (let i = 0; i < n; i++) {
    const p = dense[i]!;
    const prev = dense[i - 1] ?? p;
    const next = dense[i + 1] ?? p;
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const half = p.w / 2 + grow;
    const wl = half + wobble(p.x * 7 + p.y * 13 + i, jitter);
    const wr = half + wobble(p.x * 11 + p.y * 3 + i + 91, jitter);
    left.push({ x: p.x + nx * wl, y: p.y + ny * wl });
    right.push({ x: p.x - nx * wr, y: p.y - ny * wr });
  }
  right.reverse();
  return { points: [...left, ...right] };
}

/** One extra sample every ~26px, so an edge wobbles rather than bevels. */
function densify(pts: readonly WayPoint[]): readonly WayPoint[] {
  const out: WayPoint[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    out.push(a);
    const b = pts[i + 1];
    if (b === undefined) continue;
    const steps = Math.max(1, Math.round(Math.hypot(b.x - a.x, b.y - a.y) / 26));
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, w: a.w + (b.w - a.w) * t });
    }
  }
  return out;
}

// ------------------------------------------------------------------ gaps

interface Gap {
  readonly x: number;
  readonly w: number;
}

/**
 * The widest gaps in a band, measured on what the buildings actually *draw*.
 *
 * A back-band slot is drawn at two thirds of its slot width, so measuring the
 * gap on the slot width finds an alley that is not there.
 */
function widestGaps(
  layout: readonly TownSlot[], band: TownSlot["band"], count: number,
): readonly number[] {
  const row = layout.filter((s) => s.band === band).sort((a, b) => a.x - b.x);
  const gaps: Gap[] = [];
  for (let i = 1; i < row.length; i++) {
    const left = row[i - 1]!;
    const right = row[i]!;
    const from = left.x + drawnWidth(left) / 2;
    const to = right.x - drawnWidth(right) / 2;
    gaps.push({ x: (from + to) / 2, w: to - from });
  }
  return gaps
    .slice()
    .sort((a, b) => b.w - a.w || a.x - b.x)
    .slice(0, count)
    .map((g) => g.x)
    .sort((a, b) => a - b);
}

/** Toward the middle of the canvas: which way a run should lean from here. */
const leanFrom = (x: number): number => (x < TOWN_CANVAS_W / 2 ? 1 : -1);

// ------------------------------------------------------------------ alleys

/**
 * The alleys are derived, not authored: each one climbs out of the widest gap
 * in the band it has to cross, on a single 2:1 run leaning toward the centre of
 * the canvas. Two hundred and seventy pixels of sideways travel for a hundred
 * and forty of depth is not a mistake - that is what a street running away from
 * you looks like in this projection, and it is the cue the old vertical spurs
 * threw away.
 */
interface AlleyWay extends Way {
  /** The centre line the ribbon was grown from, so the verge can use it too. */
  readonly line: readonly WayPoint[];
}

function alleys(layout: readonly TownSlot[]): AlleyWay[] {
  const out: AlleyWay[] = [];
  const [far, mid, near] = [STREETS[0]!, STREETS[1]!, STREETS[2]!];

  const climb = (
    from: readonly WayPoint[], to: readonly WayPoint[],
    x: number, wideEnd: number, narrowEnd: number,
  ): readonly WayPoint[] => {
    const footY = centreLineAt(from, x);
    const dir = leanFrom(x);
    // Two passes: guess the far end from the nominal band spacing, then re-read
    // the street's own centre line there. One pass leaves the alley short.
    let topX = isoRun(x, footY - to[0]!.y, dir);
    let topY = centreLineAt(to, topX);
    topX = isoRun(x, footY - topY, dir);
    topY = centreLineAt(to, topX);
    return widen(
      [{ x: topX, y: topY + 2 }, { x, y: footY + 3 }],
      narrowEnd, wideEnd,
    );
  };

  for (const x of widestGaps(layout, "middle", 2)) {
    const line = climb(mid, far, x, 17, 12);
    out.push({ ...ribbon(line), band: "middle", line });
  }
  for (const x of widestGaps(layout, "front", 1)) {
    const line = climb(near, mid, x, 25, 17);
    out.push({ ...ribbon(line), band: "front", line });
  }
  return out;
}

/**
 * The near promenade: one full-bleed apron, not a row of chevrons.
 *
 * What stood here was the near street plus six approach ribbons running down
 * into it on a 2:1 lean, which tiles the bottom of the frame with alternating
 * paved and grass triangles - argyle, and the loudest untextured-polygon tell
 * on the screen. A town square is a *surface*: it runs off both edges of the
 * frame, it reaches back to under the front band's feet, and its far edge is
 * ragged because a square is bounded by what was built around it.
 *
 * The far edge rises to meet each front-band building, so every one of them
 * stands on stone rather than on a lawn with a stone patch painted under it.
 */
function plaza(layout: readonly TownSlot[], grow: number): PathPolygon {
  const front = layout.filter((s) => s.band === "front").sort((a, b) => a.x - b.x);
  const top: PathPoint[] = [];
  for (let x = -7; x <= TOWN_CANVAS_W + 7; x += 16) {
    // How near this x is to a front-band doorway, 0..1. The apron swells up
    // behind a building and falls back to the kerb line between two.
    let near = 0;
    for (const s of front) {
      const half = drawnWidth(s) / 2 + 26;
      const d = Math.abs(x - s.x);
      if (d < half) near = Math.max(near, Math.cos((d / half) * Math.PI / 2));
    }
    const base = PLAZA_KERB - 34 * near - grow;
    top.push({ x, y: base + wobble(x * 3 + 17, 5) });
  }
  return {
    points: [
      ...top,
      { x: TOWN_CANVAS_W + 7, y: PLAZA_KERB - grow + wobble(7717, 5) },
      { x: TOWN_CANVAS_W + 7, y: TOWN_CANVAS_H + 14 },
      { x: -7, y: TOWN_CANVAS_H + 14 },
    ],
  };
}

/** Where the promenade's kerb runs between the buildings that face it. */
const PLAZA_KERB = 676;

// ------------------------------------------------------------------ spurs

/** How wide the door end of a building's approach is, in drawn pixels. */
const doorWidth = (slot: TownSlot): number =>
  Math.max(20, Math.min(40, Math.round(slot.w * 0.19))) * BAND_SCALE[slot.band];

/** The street a building faces: the nearest one in front of its ground line. */
function streetInFrontOf(slot: TownSlot): number | null {
  let best: number | null = null;
  for (const way of STREETS) {
    const y = centreLineAt(way, slot.x);
    if (y > slot.y && (best === null || y < best)) best = y;
  }
  return best;
}

/**
 * The approach from one building's door down to the street it faces.
 *
 * On the same 2:1 lean as everything else, aimed at the centre of the canvas,
 * so the approaches converge as they come forward instead of dropping straight
 * down the screen like a row of pilasters.
 */
function spur(slot: TownSlot): readonly WayPoint[] | null {
  const sy = streetInFrontOf(slot);
  if (sy === null) return null;
  const d = doorWidth(slot);
  const dir = leanFrom(slot.x);
  const dy = sy - slot.y + 8;
  const foot = isoRun(slot.x, dy, dir);
  return widen(
    [
      { x: slot.x, y: slot.y - 10 },
      { x: isoRun(slot.x, dy / 2, dir), y: slot.y - 10 + dy / 2 },
      { x: foot, y: sy + 4 },
    ],
    d * 0.9, d * 1.25,
  );
}

/**
 * The whole street plan for one town.
 *
 * `layout` is every slot the faction can build on - it decides where the alleys
 * run. `occupied` is the slots with something standing on them: an empty lot
 * gets no approach, so the paving grows with the town instead of describing a
 * town that is not there yet.
 *
 * The promenade goes down first and everything else over it, so the near
 * approaches read as wear worn into one square rather than as separate ribbons
 * of a second material laid on the grass.
 */
export function townPathPlan(
  layout: readonly TownSlot[], occupied: readonly TownSlot[],
): TownPathPlan {
  const ways: Way[] = [];
  const verges: PathPolygon[] = [];
  const halos: PathPolygon[] = [];
  const push = (
    poly: PathPolygon, band: TownSlot["band"], source: readonly WayPoint[] | null,
  ): void => {
    ways.push({ points: poly.points, band });
    // Six and twenty-four rather than four and thirteen, and the jitter on
    // each roughly doubled. A critic read the streets as "full-width
    // hard-edged ribbons... a flat untextured tan band with mitred kinks and
    // no verge": a four-pixel margin under a two-pixel wobble is not a verge
    // at the 3/2 stage, it is an outline, and the halo at thirteen was inside
    // the width of the lane it was supposed to be dissolving into. What ends a
    // road on real ground is twenty or thirty pixels of trodden earth getting
    // thinner, and its edge is broken at the scale of a footfall rather than
    // of a kerbstone.
    verges.push(source === null ? poly : ribbon(source, 6, 5));
    halos.push(source === null ? poly : ribbon(source, 24, 10));
  };

  push(plaza(layout, 0), "front", null);
  halos[0] = plaza(layout, 20);
  verges[0] = plaza(layout, 6);
  push(ribbon(STREETS[0]!, 0, 3.5), "back", STREETS[0]!);
  push(ribbon(STREETS[1]!, 0, 3.5), "middle", STREETS[1]!);
  for (const a of alleys(layout)) push(a, a.band, a.line);

  // Farther first, so a nearer spur laps over the one behind it.
  const ordered = [...occupied].sort((a, b) => a.y - b.y || a.x - b.x);
  for (const slot of ordered) {
    const s = spur(slot);
    if (s !== null) push(ribbon(s), slot.band, s);
  }
  return { ways, verges, halos };
}

/** `points` in the form an SVG `<polygon>` wants. */
export const polygonPoints = (poly: PathPolygon): string =>
  poly.points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

// ------------------------------------------------------------ cobble lattice

/**
 * The paving pattern, as an isometric diamond lattice.
 *
 * The building art stands on hand-pixelled iso cobble - small warm-grey
 * diamonds with a darker joint. The street used to be axis-aligned bricks in
 * running bond, which is a *wall* seen head-on; laid next to a real apron the
 * join gave the whole ground plane away. These are the same stones: 8x4 canvas
 * pixels, the same lattice the aprons use, in the same warm palette sampled off
 * the marketplace apron.
 */
/**
 * How a band's paving is drawn: its stone, its mortar and its air.
 *
 * Stone SIZE is a function of depth, because one lattice period for the whole
 * ground plane means a stone at the viewer's feet is the same size as a stone
 * four hundred pixels into the scene. The near size is read off the
 * hand-pixelled aprons the front-band art already stands on - about eighteen
 * device pixels across at the 3/2 stage.
 *
 * The other three numbers are the mesh defect's second half, and the first
 * half - a two-stone pattern tile - did not cure it on its own. A 6x3 canvas
 * stone is 9x4.5 device pixels; a one-device-pixel mortar joint around it is a
 * third of its area, and a regular grid of hairlines at a nine-pixel period is
 * read as woven cloth however the stones inside it are toned. Measured on the
 * round-one Ironhorde shot the back street still came out as a pale
 * cross-hatch over red hardpan.
 *
 * What a real distance does to a paved surface is lose the joints first, then
 * the relief, then the contrast - the stones themselves are the last thing to
 * go. So all three are ramped:
 *
 *  - `joint` is how much of the mortar gap between two stones survives. At 0
 *    the stones tile edge to edge, there is no grid left to read, and what is
 *    left is a quilt of tones - which is what a paved street looks like from
 *    the far side of a town square.
 *  - `relief` is how much of each stone's own dome shading survives. A lit
 *    upper half and a shaded lower half inside a 9x4.5 pixel diamond is not a
 *    dome, it is a second grid at half the period.
 *  - `haze` is how far the whole texture is washed toward the town's own air.
 *    The far street measured BRIGHTER than the near plaza on Greenmantle (96.1
 *    against 82.1), which inverts every other depth cue on the screen; a wash
 *    of the same airlight the sprites take is what puts it back.
 */
export interface Paving {
  readonly w: number;
  readonly h: number;
  readonly joint: number;
  readonly relief: number;
  readonly haze: number;
  /**
   * How much VALUE the band's stones give up, as a black multiply.
   *
   * `haze` washes the texture toward the town's own air, and an air is a
   * near-neutral grey at around luma 100 - which on Tower's far street, itself
   * measured at 131.6, is a wash toward something DARKER only by accident and
   * on Emberpit's, at 46.4, is a wash toward something brighter. Aerial
   * perspective on a ground plane running away from the viewer takes value out
   * whatever the paint under it is worth, so the value half is its own number
   * and it is a multiply. Measured over device rows 352-392 against the
   * promenade at 860-915, the far ground was still BRIGHTER than the near one
   * on four of the twelve towns after the last round: Greenmantle +8.1, Tower
   * +9.6, Prismgate +14.8, Emberpit +12.2.
   */
  readonly dim: number;
}

export const COBBLE_SIZE: Readonly<Record<TownSlot["band"], Paving>> =
  Object.freeze({
    back: { w: 7, h: 3.5, joint: 0.30, relief: 0.22, haze: 0.30, dim: 0.26 },
    middle: { w: 9.5, h: 4.75, joint: 0.5, relief: 0.4, haze: 0.14, dim: 0.10 },
    front: { w: 12, h: 6, joint: 1, relief: 0.95, haze: 0, dim: 0 },
  });

export interface Cobble {
  readonly points: string;
  /** The upper half of the same diamond: the face the sun actually hits. */
  readonly lit: string;
  /** And the lower half, which is in its own shadow. */
  readonly dim: string;
  /** 0..7, so the caller can hand each stone a tone without a random source. */
  readonly tone: number;
  /**
   * How proud of the road this one stone sits, 0.30 to 1.65.
   *
   * The tone table alone cannot carry a paved surface, because eight fills
   * over two hundred stones puts an exact repeat next to itself often enough
   * to read as a repeat. What a real setted road has on top of that is stones
   * that sit at different heights: one catches the sun on its whole face and
   * the one beside it is worn flush and catches almost none. That is a
   * multiplier on this stone's own lit and shaded faces rather than a ninth
   * fill, so the palette stays eight colours wide and the SURFACE does not.
   */
  readonly proud: number;
  /**
   * One stone in seven or so, worn dark: the wheel ruts and the puddles.
   *
   * The mottle overlay carries wear at the scale of a cart track; this
   * carries it at the scale of one stone, which is the scale the critic was
   * reading the plaza at ("compare it against the hand-drawn building-platform
   * paving two rows above it in the same crop, which is real stone").
   */
  readonly worn: boolean;
}

/**
 * How many stones wide and deep one pattern tile is.
 *
 * This number is the whole of the mesh defect, and it was TWO. An SVG
 * `<pattern>` two stones across is a 24x12 canvas-pixel period at the front
 * band and a 12x6 one at the back, repeated eighty times across the frame,
 * and the per-stone jitter and tone inside it repeat with it: measured on the
 * shipped Ironhorde shot the ground's autocorrelation peaked at exactly lag
 * 9, 18 and 36 device pixels. A period that short and that exact is not read
 * as stone at all - it is read as chain-link, or as woven argyle.
 *
 * Ten stones is a 120x60 period at the front band and 60x30 at the back,
 * which is twenty-five times the area and 221 distinct stones per tile rather
 * than 8. It is still periodic - a `<pattern>` is - but it is now longer than any
 * single paved figure on the screen is wide, and the mottle overlay above it
 * runs on a 167x83 period that shares no factor with it, so the two together
 * have no visible beat. The cost is 663 polygons per band in `<defs>`, drawn
 * once.
 */
export const COBBLE_TILES = 10;

/**
 * Which of the eight stone tones a stone takes.
 *
 * A weighted draw rather than `seed % 8`, and the weights have been ROUND THE
 * HOUSES, so both ends of the mistake are written down. The eight fills in the
 * stylesheet run from luma 85 to luma 186; drawn flat they measure a standard
 * deviation of 33.7 over the table, and the previous round read that as the
 * cause of the argyle and cut it to 21.5 by pushing seven stones in eight onto
 * four middle tones. It was not the cause. The argyle was the PATTERN TILE -
 * two stones across, a 24x12 canvas period repeated eighty times - and once
 * that was ten stones across the flat draw would have been fine. Cutting the
 * spread as well bought a surface a critic measured as worse than the disease:
 * "the plaza traded periodicity for zero variance... a dead-uniform isometric
 * diamond lattice, near-identical stone tone edge to edge".
 *
 * 27.1 is the middle of the two: the four middle tones still take two stones
 * in three, so it is one quarry rather than a harlequin floor, and the four
 * outer ones take the other third rather than one stone in eight, so there is
 * something to see between one stone and its neighbour. The table is walked by
 * a hash rather than by position, so it adds no period of its own.
 */
const TONE_MIX: readonly number[] = Object.freeze([
  4, 0, 2, 5, 1, 4, 6, 0, 3, 5, 4, 1, 0, 7, 5, 2,
  4, 0, 3, 5, 1, 4, 6, 0, 5, 2, 4, 3, 1, 0, 7, 5,
]);

/** A well-mixed integer from a stone's lattice cell. */
function stoneSeed(i: number, j: number): number {
  let h = Math.imul(i, 73856093) ^ Math.imul(j, 19349663);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = Math.imul(h ^ (h >>> 15), 2246822519);
  return Math.abs(h ^ (h >>> 16));
}

/**
 * Every diamond of one pattern tile, including the ones that wrap its edges.
 *
 * Each stone carries its own lit face as well as its outline. A flat fill with
 * a joint around it reads as a checkerboard however the tones are shuffled -
 * what makes cobble read as cobble is that every stone is a little dome, lit
 * from the same upper-left sun as the buildings, and that no two neighbours are
 * quite the same size. The corners are pulled in by a per-stone hash so the
 * lattice stops being a perfect mesh, which is what made the old one read as
 * woven fabric rather than as a road.
 */
export function cobbleTile(
  cw: number, ch: number, joint: number, tiles = COBBLE_TILES,
): readonly Cobble[] {
  const out: Cobble[] = [];
  const n = tiles * 2;
  for (let i = 0; i <= n; i++) {
    for (let j = 0; j <= n; j++) {
      if ((i + j) % 2 !== 0) continue;
      const x = (i * cw) / 2;
      const y = (j * ch) / 2;
      // Hashed on the position MODULO the tile, not on the raw index: a stone
      // that straddles the tile's right edge is drawn again at its left, and
      // hashing the raw index gave the two copies different jitter and
      // different tone. That mismatch repeated on the tile's own period is a
      // line, and a line every period is the mesh.
      const seed = stoneSeed(i % n, j % n);
      // +-12% on each half-axis: neighbouring stones no longer share an edge
      // exactly, so the joint between them varies the way mortar does.
      //
      // Both the shrink and the jitter are scaled by `joint`, so a band drawn
      // at joint 0 lands on the lattice EXACTLY - every stone meets its four
      // neighbours edge to edge, no mortar shows through, and the grid the eye
      // was reading as chain-link has nothing left to read. What survives is
      // the tone, which is the one thing about a paved surface that is still
      // legible from the far side of a town square.
      const jx = (((seed >>> 3) % 9) / 8 - 0.5) * 0.24;
      const jy = (((seed >>> 9) % 9) / 8 - 0.5) * 0.24;
      const hw = (cw / 2) * (1 + (jx - 0.10) * joint);
      const hh = (ch / 2) * (1 + (jy - 0.10) * joint);
      // The two side corners walk along the joint as well, so a stone is a
      // worn quadrilateral rather than a rhombus with a size on it.
      const sk = (((seed >>> 15) % 7) / 6 - 0.5) * ch * 0.16 * joint;
      const left = `${(x - hw).toFixed(2)},${(y + sk).toFixed(2)}`;
      const top = `${x.toFixed(2)},${(y - hh).toFixed(2)}`;
      const right = `${(x + hw).toFixed(2)},${(y - sk).toFixed(2)}`;
      const bottom = `${x.toFixed(2)},${(y + hh).toFixed(2)}`;
      out.push({
        points: [top, right, bottom, left].join(" "),
        lit: [left, top, right].join(" "),
        dim: [right, bottom, left].join(" "),
        tone: TONE_MIX[(seed >>> 21) % TONE_MIX.length]!,
        proud: 0.30 + ((seed >>> 6) % 19) / 13.3,
        worn: ((seed >>> 12) % 6) === 0,
      });
    }
  }
  return out;
}
